<script setup lang="ts">
  import AiChat from '@/components/app/chat/components/AiChat.vue'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { AIChatSessionTypeEnum, type SessionGroup } from '@/types/generated'
  import { computed, onMounted } from 'vue'

  // Props for global chat configuration
  interface Props {
    sessionGroupUuid?: string
    sessionGroup?: SessionGroup
  }

  const props = withDefaults(defineProps<Props>(), {
    sessionGroupUuid: undefined,
    sessionGroup: undefined
  })

  const globalChatStore = useGlobalChatStore()

  // Load session group if we have a UUID, otherwise load standalone global chats
  onMounted(async () => {
    // Load organization users first
    await globalChatStore.loadOrganizationUsers()

    if (props.sessionGroupUuid && !props.sessionGroup) {
      await globalChatStore.loadSessionGroup(props.sessionGroupUuid)
    } else if (!props.sessionGroupUuid && !props.sessionGroup) {
      // Load standalone global chat sessions (no session group required)
      await globalChatStore.loadStandaloneGlobalChatSessions()
    }

    // Always load global prompt templates
    await globalChatStore.loadGlobalPromptTemplates()
  })

  // Use store data if available, otherwise fall back to props
  const sessionGroup = computed(() => globalChatStore.activeSessionGroup || props.sessionGroup)

  // Pass global-specific props to the shared AiChat component
  const chatProps = computed(() => ({
    sessionGroupUuid: props.sessionGroupUuid,
    sessionGroup: sessionGroup.value,
    sessionType: AIChatSessionTypeEnum.GLOBAL
  }))
</script>

<template>
  <AiChat
    v-bind="chatProps"
    data-cy="global-ai-chat-container"
  />
</template>
